// RetentionInsightCard.jsx v9, page-grammar match pass (Jul 15 2026)
//
// v9 CHANGES FROM v8 (founder-directed clarity sweep, Jul 15 2026):
// - Card shell now matches .gw-card on MyHydrationScreen EXACTLY:
//   radius 16 (was 20), padding 16 (was 20/22), marginTop 14 (was 16),
//   border #F0F1F3 (was #E7E9EC). The card read as bigger and darker than
//   its sibling cards and its Show label could not align with theirs.
// - Collapsed header is a single line (title + Show), matching the
//   "Why your target is N oz today" card's collapsed grammar. The
//   HYDRATION EFFICIENCY eyebrow moved INSIDE the expanded body as the
//   section opener; branding renders once the user asks for depth.
// - Absorbed the policy line relocated from the Retained Hydration card:
//   "Only bottle-verified water moves your retained total. Manual logs are
//   tracked on the My Intake tab." It opens the expanded body. Relocated
//   from IntakeBar, not new copy.
//
// (v8 header preserved below for provenance.)
// RetentionInsightCard.jsx v8, Hydration Efficiency surgical pass (Jul 14 2026)
// Generosity WTR App, My Hydration page
// Collapsible card matching the "Why your target is 76 oz today" pattern.
//
// v8 CHANGES FROM v7 (surgical UI pass, founder-directed, Jul 14 2026):
// - Renamed: "Retention Intelligence" -> "Hydration Efficiency";
//   "How your retained ounces work" -> dynamic "Why {verified} oz became
//   {retained} oz" (falls back to "Why verified becomes retained" before
//   the first verified fill).
// - Typography normalized to the app scale: eyebrow 11px, title 15px
//   (gw-cardtitle scale), Show/Hide 13px. The 20px title and 17px toggle
//   overpowered the page.
// - Stat strip labels: VERIFIED INTAKE / RETAINED HYDRATION / OF DAILY GOAL.
// - Gauge word: RETAINED (was RETENTION). Earned caption matches.
// - Factor section header: "What's Moving Your Hydration Today" (plain
//   section label). WTR Intel(TM) is a badge for AI-generated context,
//   never a section title (founder ruling, item 9).
// - Tier chips: the "developing" chip is no longer rendered anywhere on
//   this card (visual de-busying). verified / corroborated chips remain.
//   FOUNDER-RULING BOUNDARY (do not cross without explicit direction):
//   the plain-language honesty line "An estimate, not a body measurement."
//   STAYS. The chip was removed; the disclosure was not. Tier data still
//   arrives in the payload untouched; this is presentation only.
//
// HERO: the Generosity(TM) ring as a live gauge.
//   Active state: ring sweeps goal progress. Inside the ring, the
//     Hydration Score gauge typography exactly: tracked eyebrow,
//     large tone-colored number, state word, light blue projection.
//     No droplet. The droplet is not decoration, it is the reward.
//   Earned state (100%): every animation stops. The droplet appears
//     and the graphic renders as the static, solid Generosity logo.
//
// GEOMETRY IS MEASURED, NOT ESTIMATED. Ring stroke, radii, droplet
// silhouette, and TM placement were extracted from the master asset
// (G_Assets__Drop_Blue.png). The droplet bezier path below validates
// at 0.9874 IoU against the actual mark at full resolution. Do not
// hand-edit the path or ring constants; re-derive from the asset.
//
// HARD RULES:
// 1. No retention math in this file. The server sends every number
//    (fillPct, ringPct, earned) and every string (stateWord, subline).
// 2. Numeric factor deltas render only if server emits factor.deltaLabel.
// 3. Three USPTO phrases render verbatim. Do not edit their wording.
// 4. Every intelligence claim carries a confidence tier IN THE PAYLOAD;
//    v8 renders chips only for verified/corroborated and keeps the
//    estimate disclosure line as the user-facing honesty floor.
// 5. Motion honors prefers-reduced-motion and is absent in the
//    earned state by design.
//
// PAYLOAD NOTE FOR ETHAN: keep `subline` at 18 characters or fewer.
// It renders low in the ring where the chord narrows.

import React, { useEffect, useState } from "react";

const BRAND = {
  blue: "#51B0E6",
  blueDeep: "#3E97CC",
  blueWash: "#EAF6FD",
  gray: "#A6A8AB",
  lightGray: "#F0F1F3",
  hairline: "#E7E9EC",
  ink: "#0F1419",
  white: "#FFFFFF",
  green: "#4A8A6F",
  amber: "#B08A3E",
};

const FONT = {
  body: "Montserrat, 'Helvetica Neue', sans-serif",
  label: "'Barlow Condensed', 'Arial Narrow', sans-serif",
};

const TIER_STYLE = {
  verified: BRAND.green,
  corroborated: BRAND.green,
  developing: BRAND.amber,
  hypothesis_only: BRAND.gray,
};

const TONE_COLOR = {
  good: BRAND.blueDeep,
  behind: BRAND.amber,
};

// Measured logo geometry, derived from the master asset.
// Asset: ring outer R 346px, inner R 273px, stroke 73px, droplet
// height 56.2% of inner diameter, max width 31.1%, midpoint 4.5px
// below ring center. Scaled to a 320-wide viewBox, center (160,130).
const LOGO = {
  cx: 160,
  cy: 130,
  R: 96, // stroke centerline radius
  RING_W: 22.7,
  DROP_PATH:
    "M160 83.7 C162 90 173.5 114 182.3 133 C185.2 138 186.4 143 186.4 147.7 C186.4 167.5 176.5 179 160 179 C143.5 179 133.6 167.5 133.6 147.7 C133.6 143 134.8 138 137.7 133 C146.5 114 158 90 160 83.7 Z",
  TM: { x: 254, y: 238 },
  VIEW: "0 0 320 252",
  HEIGHT: 252,
};

function TierBadge({ tier }) {
  /* v8: the developing / hypothesis_only chips no longer render on this
     card (surgical de-busying, Jul 14 2026). The claim's honesty is
     carried by the plain-language disclosure line and by conservative
     copy; the tier still arrives in the payload and verified /
     corroborated chips still render as trust signals. */
  if (tier !== "verified" && tier !== "corroborated") return null;
  const c = TIER_STYLE[tier] || BRAND.amber;
  return (
    <span
      style={{
        display: "inline-block",
        border: `1px solid ${c}`,
        color: c,
        borderRadius: 999,
        padding: "1px 10px",
        fontSize: 11,
        fontWeight: 600,
        fontFamily: FONT.body,
        letterSpacing: "0.02em",
        whiteSpace: "nowrap",
      }}
    >
      {tier}
    </span>
  );
}

// -----------------------------------------------------------------
// Earned state: the logo itself. Solid, flat, static, complete.
// -----------------------------------------------------------------
function EarnedLogo() {
  return (
    <svg
      width="100%"
      height={LOGO.HEIGHT}
      viewBox={LOGO.VIEW}
      style={{ maxWidth: 300, display: "block", margin: "0 auto" }}
      role="img"
      aria-label="Goal met. One hundred percent retained toward goal. The Generosity mark, complete."
    >
      <circle cx={LOGO.cx} cy={LOGO.cy} r={LOGO.R} fill="none" stroke={BRAND.blue} strokeWidth={LOGO.RING_W} />
      <path d={LOGO.DROP_PATH} fill={BRAND.blue} />
      <text x={LOGO.TM.x} y={LOGO.TM.y} fontFamily={FONT.body} fontWeight="700" fontSize="11" fill={BRAND.blue}>
        {"\u2122"}
      </text>
    </svg>
  );
}

// -----------------------------------------------------------------
// Active state: the ring as a gauge, Hydration Score typography
// inside. Ring sweeps to ringPct from 12 o'clock in flat brand
// blue, so a completed arc is already the logo ring.
// -----------------------------------------------------------------
function RetentionGauge({ ringPct, fillPct, stateWord, subline, tone }) {
  const ring = Math.max(0, Math.min(100, ringPct));
  const fill = Math.max(0, Math.min(100, fillPct));
  const toneColor = TONE_COLOR[tone] || BRAND.blueDeep;

  const { cx, cy, R, RING_W } = LOGO;
  const CIRC = 2 * Math.PI * R;

  const [sweep, setSweep] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setSweep(ring), 60);
    return () => clearTimeout(t);
  }, [ring]);
  const dashOffset = CIRC * (1 - sweep / 100);

  const endAngle = (-90 + 3.6 * sweep) * (Math.PI / 180);
  const dotX = cx + R * Math.cos(endAngle);
  const dotY = cy + R * Math.sin(endAngle);

  return (
    <div style={{ textAlign: "center" }}>
      <style>{`
        .gw-ring-arc {
          transition: stroke-dashoffset 1.5s cubic-bezier(0.22, 0.68, 0.31, 1);
        }
        .gw-ring-dot {
          transition: cx 1.5s cubic-bezier(0.22, 0.68, 0.31, 1),
                      cy 1.5s cubic-bezier(0.22, 0.68, 0.31, 1);
        }
        @media (prefers-reduced-motion: reduce) {
          .gw-ring-arc, .gw-ring-dot { transition: none !important; }
        }
      `}</style>

      <svg
        width="100%"
        height={LOGO.HEIGHT}
        viewBox={LOGO.VIEW}
        style={{ maxWidth: 300, display: "block", margin: "0 auto" }}
        role="img"
        aria-label={`Retained ${fill} percent, ${stateWord}. Goal progress ${ring} percent.`}
      >
        {/* ring track */}
        <circle cx={cx} cy={cy} r={R} fill="none" stroke={BRAND.blueWash} strokeWidth={RING_W} />

        {/* progress arc, flat brand blue so completion equals the logo ring */}
        <circle
          className="gw-ring-arc"
          cx={cx}
          cy={cy}
          r={R}
          fill="none"
          stroke={BRAND.blue}
          strokeWidth={RING_W}
          strokeLinecap="round"
          strokeDasharray={CIRC}
          strokeDashoffset={dashOffset}
          transform={`rotate(-90 ${cx} ${cy})`}
        />

        {/* arc end dot */}
        {sweep > 0 && (
          <circle
            className="gw-ring-dot"
            cx={dotX}
            cy={dotY}
            r="7"
            fill={BRAND.white}
            stroke={BRAND.blueDeep}
            strokeWidth="2.5"
          />
        )}

        {/* Hydration Score gauge typography, mirrored */}
        <text x={cx} y="96" textAnchor="middle"
          fontFamily={FONT.body} fontWeight="600" fontSize="11"
          letterSpacing="0.3em" fill={BRAND.gray}>
          RETAINED
        </text>

        {/* number centered to RETENTION, percent superscripted top right */}
        <text x={cx} y="148" textAnchor="middle"
          fontFamily={FONT.body} fontWeight="800" fontSize="58" fill={toneColor}>
          {fill}
        </text>
        <text
          x={cx + (String(fill).length * 36) / 2 + 4}
          y="120"
          textAnchor="start"
          fontFamily={FONT.body}
          fontWeight="800"
          fontSize="22"
          fill={toneColor}
        >
          %
        </text>

        <text x={cx} y="174" textAnchor="middle"
          fontFamily={FONT.body} fontWeight="700" fontSize="17" fill={toneColor}>
          {stateWord}
        </text>

        {subline && (
          <text x={cx} y="194" textAnchor="middle"
            fontFamily={FONT.body} fontWeight="500" fontSize="12" fill={BRAND.blue}>
            {subline}
          </text>
        )}

        {/* trademark, placed as on the brand asset */}
        <text x={LOGO.TM.x} y={LOGO.TM.y} fontFamily={FONT.body} fontWeight="700" fontSize="11" fill={BRAND.blue}>
          {"\u2122"}
        </text>
      </svg>
    </div>
  );
}

// -----------------------------------------------------------------
// Earned caption, beneath the logo. Same gauge voice, compact.
// -----------------------------------------------------------------
function EarnedCaption({ stateWord }) {
  return (
    <div style={{ textAlign: "center", marginTop: 8 }}>
      <div
        style={{
          fontFamily: FONT.body,
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: "0.3em",
          textTransform: "uppercase",
          color: BRAND.gray,
        }}
      >
        Retained
      </div>
      <div style={{ fontFamily: FONT.body, fontSize: 34, fontWeight: 800, color: BRAND.blue, marginTop: 2 }}>
        100%
      </div>
      <div style={{ fontFamily: FONT.body, fontSize: 16, fontWeight: 700, color: BRAND.blue }}>
        {stateWord || "Goal Met"}
      </div>
    </div>
  );
}

function StatStrip({ verifiedOz, retainedOz, ringPct }) {
  const stat = (value, unit, label) => (
    <div style={{ flex: 1, textAlign: "center", padding: "2px 6px" }}>
      <div style={{ fontFamily: FONT.body, fontSize: 19, fontWeight: 800, color: BRAND.ink }}>
        {value}
        {unit && (
          <span style={{ fontSize: 12, fontWeight: 600, color: BRAND.gray, marginLeft: 2 }}>
            {unit}
          </span>
        )}
      </div>
      <div
        style={{
          fontFamily: FONT.label,
          fontSize: 12,
          fontWeight: 600,
          letterSpacing: "0.12em",
          color: BRAND.gray,
          marginTop: 1,
        }}
      >
        {label}
      </div>
    </div>
  );

  const divider = <div style={{ width: 1, background: BRAND.hairline, margin: "4px 0" }} />;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "stretch", marginTop: 18 }}>
        {stat(verifiedOz, "oz", "VERIFIED INTAKE")}
        {divider}
        {stat(retainedOz, "oz", "RETAINED HYDRATION")}
        {divider}
        {stat(`${ringPct}%`, null, "OF DAILY GOAL")}
      </div>
      <div
        style={{
          textAlign: "center",
          fontFamily: FONT.body,
          fontSize: 11,
          color: BRAND.gray,
          marginTop: 8,
        }}
      >
        Only bottle-verified water moves this ring.
      </div>
    </div>
  );
}

// -----------------------------------------------------------------
// WTR Intel factor card. Four elements: eyebrow, state headline,
// live data chip, tier. One detail line. Left rail encodes direction.
// -----------------------------------------------------------------
function IntelFactorCard({ factor }) {
  const railColor =
    factor.direction === "up"
      ? BRAND.green
      : factor.direction === "down"
      ? BRAND.amber
      : BRAND.gray;
  const glyph =
    factor.direction === "up" ? "\u2191" : factor.direction === "down" ? "\u2193" : "\u2192";

  return (
    <div
      style={{
        position: "relative",
        background: BRAND.white,
        border: `1px solid ${BRAND.hairline}`,
        borderRadius: 16,
        padding: "14px 16px 14px 20px",
        overflow: "hidden",
        boxShadow: "0 1px 2px rgba(15,20,25,0.04)",
      }}
    >
      <div
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: 3,
          background: railColor,
        }}
      />

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
        }}
      >
        <span
          style={{
            fontFamily: FONT.label,
            fontSize: 13,
            fontWeight: 600,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: BRAND.gray,
          }}
        >
          {factor.label}
        </span>
        {factor.metric && (
          <span
            style={{
              fontFamily: FONT.body,
              fontSize: 12,
              fontWeight: 700,
              color: BRAND.ink,
              background: BRAND.lightGray,
              borderRadius: 999,
              padding: "3px 11px",
              whiteSpace: "nowrap",
            }}
          >
            {factor.metric}
          </span>
        )}
      </div>

      <div
        style={{
          fontFamily: FONT.body,
          fontSize: 15,
          fontWeight: 700,
          color: BRAND.ink,
          marginTop: 6,
          lineHeight: 1.35,
        }}
      >
        <span style={{ color: railColor, marginRight: 6, fontWeight: 800 }}>{glyph}</span>
        {factor.state}
        {factor.deltaLabel ? (
          <span style={{ color: railColor, fontWeight: 700 }}> {factor.deltaLabel}</span>
        ) : null}
      </div>

      <div
        style={{
          fontFamily: FONT.body,
          fontSize: 13,
          color: BRAND.gray,
          marginTop: 4,
          lineHeight: 1.5,
        }}
      >
        {factor.detail}
      </div>

      <div style={{ marginTop: 8 }}>
        <TierBadge tier={factor.tier} />
      </div>
    </div>
  );
}

// -----------------------------------------------------------------
// Mock payload. Ethan replaces via the `retention` prop.
// Every number and string here is server-authored in production.
// `earned` flips the hero to the static logo. The server owns the
// milestone definition; the client only obeys the flag.
// -----------------------------------------------------------------
const MOCK_RETENTION = {
  retainedOz: 63,
  verifiedOz: 76,
  manualOz: 8,
  goalOz: 76,
  ringPct: 82,
  fillPct: 82,
  earned: false,
  stateWord: "Building",
  subline: "13 oz to go",
  tone: "good",
  tier: "developing",
  factors: [
    {
      id: "verified_share",
      label: "Verified Intake",
      metric: "76 oz",
      state: "Anchoring your estimate",
      direction: "up",
      detail:
        "Measured directly from your bottle. Only verified water enters your retained hydration estimate.",
      tier: "verified",
      deltaLabel: null,
    },
    {
      id: "water_quality",
      label: "Water Quality",
      metric: "Score 82",
      state: "Raising retained hydration",
      direction: "up",
      detail:
        "Filtered, mineral-bearing fills. Minerals and electrolytes help your body hold what you drink.",
      tier: "corroborated",
      deltaLabel: null,
    },
    {
      id: "environment",
      label: "Environment",
      metric: "Hot",
      state: "Lowering retained hydration",
      direction: "down",
      detail: "High heat increases fluid loss, so a larger share of intake is released.",
      tier: "corroborated",
      deltaLabel: null,
    },
    {
      id: "recovery",
      label: "Recovery",
      metric: "Readiness 81",
      state: "Personalizing your band",
      direction: "neutral",
      detail:
        "Your wearable context personalizes the estimate\u2019s range. Recovery can shift how your body manages fluid balance.",
      tier: "developing",
      deltaLabel: null,
    },
  ],
};

export default function RetentionInsightCard({ retention, ci, onOpenEpiphany }) {
  const [open, setOpen] = useState(false);
  const data = retention || MOCK_RETENTION;
  const earned = Boolean(data.earned);

  /* v8: dynamic title, the card's promise in one line. Reads the two
     server numbers it already renders in the stat strip; computes
     nothing. Before the first verified fill, falls back to the generic
     phrasing rather than "Why 0 oz became 0 oz". */
  const cardTitle =
    data.verifiedOz > 0
      ? `Why ${data.verifiedOz} oz became ${data.retainedOz} oz`
      : "Why verified becomes retained";

  return (
    <div
      style={{
        background: BRAND.white,
        border: `1px solid ${BRAND.lightGray}`,
        borderRadius: 16,
        padding: 16,
        fontFamily: FONT.body,
        color: BRAND.ink,
        marginTop: 14,
      }}
    >
      <button
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          background: "none",
          border: "none",
          padding: 0,
          cursor: "pointer",
          fontFamily: "inherit",
        }}
      >
        <span
          style={{
            textAlign: "left",
            fontSize: 15,
            fontWeight: 700,
            color: BRAND.ink,
            lineHeight: 1.3,
          }}
        >
          {cardTitle}
        </span>
        <span style={{ fontSize: 13, color: BRAND.gray, marginLeft: 12, whiteSpace: "nowrap" }}>
          {open ? "Hide" : "Show"}
        </span>
      </button>

      {open && (
        <div style={{ marginTop: 16 }}>
          {/* v9: the eyebrow opens the expanded body (moved from the
              collapsed header so collapsed cards share one grammar). */}
          <div
            style={{
              fontFamily: FONT.label,
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: BRAND.blueDeep,
            }}
          >
            Hydration Efficiency
          </div>
          {/* Relocated from the Retained Hydration card (Jul 15 2026
              three-second pass): the policy line lives here, on the
              surface whose job is explaining verified vs retained. */}
          <div style={{ fontSize: 12, color: BRAND.gray, lineHeight: 1.5, margin: "6px 0 16px" }}>
            Only bottle-verified water moves your retained total. Manual logs
            are tracked on the My Intake tab.
          </div>
          {/* 1. The hero: live gauge, or the earned logo */}
          {earned ? (
            <>
              <EarnedLogo />
              <EarnedCaption stateWord={data.stateWord} />
            </>
          ) : (
            <RetentionGauge
              ringPct={data.ringPct}
              fillPct={data.fillPct}
              stateWord={data.stateWord}
              subline={data.subline}
              tone={data.tone}
            />
          )}

          <StatStrip
            verifiedOz={data.verifiedOz}
            retainedOz={data.retainedOz}
            ringPct={data.ringPct}
          />

          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 8,
              marginTop: 14,
            }}
          >
            {/* v8: overall tier chip removed (was <TierBadge tier={data.tier} />).
                The disclosure line below is the honesty floor and STAYS.
                Removing it requires an explicit founder ruling; it is not
                cosmetic. Tier still ships in the payload. */}
            <span style={{ fontSize: 12, color: BRAND.gray }}>
              An estimate, not a body measurement.
            </span>
          </div>

          {ci && ci.low && ci.high && (
            <div style={{ textAlign: "center", fontSize: 11, color: BRAND.gray, marginTop: 5 }}>
              Estimated range {ci.low.val} to {ci.high.val} {ci.high.unit}
            </div>
          )}

          {/* 2. WTR Intel factor cards */}
          <div
            style={{
              fontFamily: FONT.label,
              fontSize: 13,
              fontWeight: 600,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: BRAND.gray,
              margin: "26px 0 10px",
            }}
          >
            What{"\u2019"}s Moving Your Hydration Today
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {data.factors.map((f) => (
              <IntelFactorCard key={f.id} factor={f} />
            ))}
          </div>

          {/* 3. Takeaway band */}
          <div
            style={{
              marginTop: 22,
              background: `linear-gradient(135deg, ${BRAND.blueWash}, #F7FBFE)`,
              border: "1px solid #D9EEFA",
              borderRadius: 16,
              padding: "16px 18px",
            }}
          >
            <div style={{ fontSize: 17, fontWeight: 800, color: BRAND.ink }}>
              Drink better, not just more.
            </div>
            <div style={{ fontSize: 13, color: BRAND.blueDeep, fontWeight: 600, marginTop: 4 }}>
              Harmful TDS out. Quality minerals in. More of every ounce stays.
            </div>
          </div>

          {/* 4. USPTO phrases, verbatim */}
          <div
            style={{
              marginTop: 18,
              paddingTop: 14,
              borderTop: `1px solid ${BRAND.hairline}`,
              fontSize: 12,
              color: BRAND.gray,
              lineHeight: 1.9,
            }}
          >
            Absorption, not just intake.
            <br />
            Absorption efficiency model, not a concentration adjustment model.
            <br />
            Adaptive range, not a fixed threshold.
          </div>

          {onOpenEpiphany && (
            <button
              onClick={onOpenEpiphany}
              style={{
                display: "block",
                background: "none",
                border: "none",
                padding: 0,
                marginTop: 12,
                cursor: "pointer",
                fontFamily: FONT.body,
                fontSize: 13,
                fontWeight: 700,
                color: BRAND.blueDeep,
              }}
            >
              Read The Hydration Epiphany
            </button>
          )}

          {/* 5. Fine print */}
          <div
            style={{
              fontSize: 11,
              color: BRAND.gray,
              marginTop: 10,
              lineHeight: 1.6,
              opacity: 0.85,
            }}
          >
            Retained ounces are a general wellness estimate from your logged data
            and connected devices, not a medical measurement or medical advice.
          </div>
        </div>
      )}
    </div>
  );
}
